import React, { useState, useEffect, useRef } from 'react';
import { Upload, Download, Play, Pause, RotateCcw, Users, Settings } from 'lucide-react';

const DraftPickerApp = () => {
  const [players, setPlayers] = useState([]);
  const [picks, setPicks] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState('');
  const [weightType, setWeightType] = useState('equal'); // 'equal', 'percentage', 'raw'
  const [orderType, setOrderType] = useState('descending'); // 'ascending', 'descending'
  const [totalPicks, setTotalPicks] = useState(10);
  const [isPickingActive, setIsPickingActive] = useState(false);
  const [currentPickNumber, setCurrentPickNumber] = useState(1);
  const [cyclingPlayer, setCyclingPlayer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(100);
  
  const [autoMode, setAutoMode] = useState(false);
  const [showModal, setShowModal] = useState(null); 

  const fileInputRef = useRef(null);
  const cycleIntervalRef = useRef(null);
  const autoTimeoutRef = useRef(null);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (autoTimeoutRef.current) {
        clearTimeout(autoTimeoutRef.current);
      }
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
      }
    };
  }, []);

  // Auto-continue effect when picks change
  useEffect(() => {
    if (autoMode && showResult && picks.length < totalPicks && picks.length > 0) {
      autoTimeoutRef.current = setTimeout(() => {
        setShowResult(false);
        setTimeout(() => {
          startPicking();
        }, 500);
      }, 3000);
    }
  }, [picks.length, autoMode, showResult, totalPicks]);

  // Add a new player manually
  const addPlayer = () => {
    if (currentPlayer.trim() && !players.find(p => p.name === currentPlayer.trim())) {
      const newPlayer = {
        id: Date.now(),
        name: currentPlayer.trim(),
        weight: weightType === 'equal' ? 1 : 50
      };
      setPlayers([...players, newPlayer]);
      setCurrentPlayer('');
    }
  };

  // Remove a player
  const removePlayer = (id) => {
    setPlayers(players.filter(p => p.id !== id));
  };

  // Update player weight
  const updatePlayerWeight = (id, weight) => {
    setPlayers(players.map(p => p.id === id ? { ...p, weight: parseFloat(weight) || 0 } : p));
  };

  // Import players from file
  const importPlayers = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          let importedPlayers = [];
          
          if (file.name.endsWith('.json')) {
            importedPlayers = JSON.parse(content);
          } else {
            // Assume CSV or text file
            const lines = content.split('\n').filter(line => line.trim());
            importedPlayers = lines.map((line, index) => {
              const parts = line.split(',').map(part => part.trim());
              return {
                id: Date.now() + index,
                name: parts[0],
                weight: parts[1] ? parseFloat(parts[1]) || 1 : 1
              };
            });
          }
          
          setPlayers(importedPlayers.filter(p => p.name));
        } catch (error) {
          alert('Error importing file. Please check the format.');
        }
      };
      reader.readAsText(file);
    }
  };

  // Export players to JSON
  const exportPlayers = () => {
    const dataStr = JSON.stringify(players, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = 'players.json';
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Weighted random selection
  const weightedRandomSelect = (availablePlayers) => {
    if (weightType === 'equal') {
      return availablePlayers[Math.floor(Math.random() * availablePlayers.length)];
    }
    
    const totalWeight = availablePlayers.reduce((sum, player) => sum + player.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (let player of availablePlayers) {
      random -= player.weight;
      if (random <= 0) {
        return player;
      }
    }
    
    return availablePlayers[0];
  };

  // Start the picking animation
  const startPicking = () => {
    // Clear any existing timeouts
    if (autoTimeoutRef.current) {
      clearTimeout(autoTimeoutRef.current);
      autoTimeoutRef.current = null;
    }

    if (players.length === 0 || picks.length >= totalPicks) return;
    
    setIsPickingActive(true);
    setShowResult(false);
    
    const availablePlayers = players.filter(p => !picks.find(pick => pick.player.id === p.id));
    if (availablePlayers.length === 0) {
      setIsPickingActive(false);
      return;
    }
    
    let cycleCount = 0;
    const maxCycles = 20 + Math.floor(Math.random() * 20);
    
    cycleIntervalRef.current = setInterval(() => {
      if (cycleCount < maxCycles) {
        const randomPlayer = availablePlayers[Math.floor(Math.random() * availablePlayers.length)];
        setCyclingPlayer(randomPlayer.name);
        cycleCount++;
      } else {
        // Final selection
        const selectedPlayer = weightedRandomSelect(availablePlayers);
        setCyclingPlayer(selectedPlayer.name);
        clearInterval(cycleIntervalRef.current);
        
        setTimeout(() => {
          // Add the pick
          const pickNumber = orderType === 'descending' ? currentPickNumber : totalPicks - currentPickNumber + 1;
          const newPick = {
            pickNumber: pickNumber,
            player: selectedPlayer,
            timestamp: new Date().toLocaleTimeString()
          };
          
          setPicks(prevPicks => [...prevPicks, newPick]);
          setCurrentPickNumber(prev => prev + 1);
          setShowResult(true);
          setIsPickingActive(false);
          setCyclingPlayer('');
        }, 1500);
      }
    }, animationSpeed);
  };

  // Function to start the next pick (for manual continue)
  const startNextPick = () => {
    if (picks.length < totalPicks) {
      setShowResult(false);
      setTimeout(() => {
        startPicking();
      }, 500);
    }
  };

  // Reset the draft
  const resetDraft = () => {
    setPicks([]);
    setCurrentPickNumber(1);
    setIsPickingActive(false);
    setShowResult(false);
    setCyclingPlayer('');
    if (cycleIntervalRef.current) {
      clearInterval(cycleIntervalRef.current);
    }
  };

  // Modal content
  const getModalContent = () => {
    switch(showModal) {
      case 'privacy':
        return {
          title: 'Privacy Policy',
          content: (
            <div className="space-y-4 text-white/80">
              <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Information We Collect</h3>
                <p>We may collect information you provide directly to us, such as when you use our draft picker tool. We do not store personal information on our servers.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">How We Use Information</h3>
                <p>We use the information to provide and improve our services. All draft data is stored locally in your browser.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Cookies and Analytics</h3>
                <p>We use Google Analytics and Google AdSense, which may use cookies to provide relevant advertisements and analyze site usage.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Third-Party Services</h3>
                <p>Our site uses Google AdSense to display advertisements. Google's use of advertising cookies enables it to serve ads based on your visits to our site and other sites on the Internet.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Contact Us</h3>
                <p>If you have questions about this Privacy Policy, contact us through our contact page.</p>
              </div>
            </div>
          )
        };
      case 'terms':
        return {
          title: 'Terms of Service',
          content: (
            <div className="space-y-4 text-white/80">
              <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Acceptance of Terms</h3>
                <p>By using DraftPickerPro™, you agree to these terms of service.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Use of Service</h3>
                <p>You may use our service for personal, non-commercial purposes. The service is provided free of charge.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Prohibited Uses</h3>
                <p>You may not use our service for any illegal or unauthorized purpose. You may not modify, distribute, or reverse engineer our service.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Intellectual Property</h3>
                <p>DraftPickerPro™ and all related trademarks are the property of DraftPickerPro. All rights reserved.</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Disclaimer</h3>
                <p>DraftPickerPro™ is provided "as is" without warranties of any kind. We are not responsible for draft outcomes or decisions made using our tool.</p>
              </div>
            </div>
          )
        };
      case 'contact':
        return {
          title: 'Contact Us',
          content: (
            <div className="space-y-4 text-white/80">
              <p>We'd love to hear from you! Get in touch with any questions, suggestions, or feedback.</p>
              
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">General Inquiries</h3>
                  <p>For general questions about DraftPickerPro™, please <a href="mailto:draftpickerpro@gmail.com" className="text-blue-300 hover:text-blue-200 underline transition-colors">email us</a> at and we'll get back to you within 24-48 hours.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Bug Reports</h3>
                  <p>Found a bug? Help us improve by reporting it. Please include details about what you were doing when the issue occurred.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Feature Requests</h3>
                  <p>Have an idea for a new feature? We're always looking to improve DraftPickerPro™ based on user feedback.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Business Inquiries</h3>
                  <p>For partnership opportunities or business-related questions, please reach out through our business contact.</p>
                </div>
              </div>
            </div>
          )
        };
      default:
        return null;
    }
  };

  // Create draft board with all pick slots (always in 1-10 visual order)
  const createDraftBoard = () => {
    const board = [];
    for (let visualPosition = 1; visualPosition <= totalPicks; visualPosition++) {
      const existingPick = picks.find(pick => pick.pickNumber === visualPosition);
      board.push({
        visualPosition: visualPosition,
        pickNumber: visualPosition,
        pick: existingPick || null
      });
    }
    return board;
  };

  const draftBoard = createDraftBoard();
  const availablePlayersCount = players.filter(p => !picks.find(pick => pick.player.id === p.id)).length;
  const canPick = availablePlayersCount > 0 && picks.length < totalPicks && !isPickingActive;

  return (
    <>
    <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
          transition: background 0.2s ease;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
        }

        /* Fix dropdown styling */
        select option {
          background-color: #4c1d95 !important;
          color: white !important;
        }

        select:focus option {
          background-color: #4c1d95 !important;
          color: white !important;
        }

        select option:hover {
          background-color: #5b21b6 !important;
          color: white !important;
        }
      `}</style>
    
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-white text-center mb-8">
          🏆 Draft Picker Pro™
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Configuration */}
          <div className="space-y-6">
            {/* Player Input */}
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Manage Players
              </h2>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={currentPlayer}
                  onChange={(e) => setCurrentPlayer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addPlayer()}
                  placeholder="Enter player name"
                  className="flex-1 px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  onClick={addPlayer}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Add
                </button>
              </div>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={importPlayers}
                  accept=".json,.csv,.txt"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Import
                </button>
                <button
                  onClick={exportPlayers}
                  disabled={players.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
              
              <div className="max-h-48 overflow-y-auto space-y-2 custom-scrollbar" style={{ paddingRight: '8px' }}>
                {players.map((player) => (
                  <div key={player.id} className="flex items-center gap-2 bg-white/10 rounded-lg p-3">
                    <span className="flex-1 text-white">{player.name}</span>
                    {weightType !== 'equal' && (
                      <input
                        type="number"
                        value={player.weight}
                        onChange={(e) => updatePlayerWeight(player.id, e.target.value)}
                        className="w-20 px-2 py-1 bg-white/20 border border-white/30 rounded text-white text-sm"
                        step={weightType === 'percentage' ? 1 : 0.1}
                        min="0"
                        max={weightType === 'percentage' ? 100 : undefined}
                      />
                    )}
                    <button
                      onClick={() => removePlayer(player.id)}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="text-sm text-white/80 mt-2">
                Total Players: {players.length} | Available: {availablePlayersCount}
              </div>
            </div>
            
            {/* Settings */}
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Draft Settings
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Weight Type</label>
                  <select
                    value={weightType}
                    onChange={(e) => setWeightType(e.target.value)}
                    className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="equal">Equal Weight</option>
                    <option value="percentage">Percentage (0-100)</option>
                    <option value="raw">Raw Values</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm mb-2">Pick Order</label>
                  <select
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value)}
                    className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="descending">Descending (1st pick first)</option>
                    <option value="ascending">Ascending (Last pick first)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm mb-2">Total Picks</label>
                  <input
                    type="number"
                    value={totalPicks}
                    onChange={(e) => setTotalPicks(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    max={players.length}
                    className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm mb-2">Animation Speed (ms)</label>
                  <input
                    type="range"
                    value={animationSpeed}
                    onChange={(e) => setAnimationSpeed(parseInt(e.target.value))}
                    min="50"
                    max="300"
                    className="w-full"
                  />
                  <div className="text-white/60 text-xs text-center">{animationSpeed}ms</div>
                </div>
                
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-white/80">
                    <input
                      type="checkbox"
                      checked={autoMode}
                      onChange={(e) => setAutoMode(e.target.checked)}
                      className="rounded"
                    />
                    Auto Mode (automatically continue to next pick)
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Panel - Draft Process */}
          <div className="space-y-6 h-full overflow-visible">
            {/* Draft Controls */}
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-center">
                <div className="text-6xl font-bold text-white mb-4">
                  {isPickingActive ? '🎲' : showResult ? '🎉' : '⚡'}
                </div>
                
                <div className="text-2xl font-semibold text-white mb-2">
                  Pick #{orderType === 'descending' ? currentPickNumber : totalPicks - currentPickNumber + 1}
                </div>
                
                <div className="text-lg text-white/80 mb-6">
                  {picks.length} of {totalPicks} picks completed
                </div>
                
                <div className="h-16 flex items-center justify-center mb-6">
                  {isPickingActive && (
                    <div className="text-3xl font-bold text-yellow-300 animate-pulse">
                      {cyclingPlayer}
                    </div>
                  )}
                  {showResult && picks.length > 0 && (
                    <div className="text-3xl font-bold text-green-300 animate-bounce">
                      {picks[picks.length - 1].player.name}
                    </div>
                  )}
                  {!isPickingActive && !showResult && (
                    <div className="text-xl text-white/60">
                      Ready to pick!
                    </div>
                  )}
                </div>
                
                <div className="flex gap-4 justify-center">
                  {showResult && !autoMode && picks.length < totalPicks ? (
                    <button
                      onClick={startNextPick}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-lg"
                    >
                      <Play className="w-5 h-5" />
                      Continue to Next Pick
                    </button>
                  ) : !showResult && !isPickingActive && picks.length < totalPicks ? (
                    <button
                      onClick={startPicking}
                      disabled={!canPick}
                      className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors text-lg"
                    >
                      <Play className="w-5 h-5" />
                      Start Pick
                    </button>
                  ) : isPickingActive ? (
                    <button
                      disabled
                      className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium transition-colors text-lg"
                    >
                      <Play className="w-5 h-5" />
                      Picking...
                    </button>
                  ) : picks.length >= totalPicks ? (
                    <button
                      disabled
                      className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium transition-colors text-lg"
                    >
                      Draft Complete!
                    </button>
                  ) : null}
                  
                  <button
                    onClick={resetDraft}
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors text-lg"
                  >
                    <RotateCcw className="w-5 h-5" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
            
            {/* Draft Board */}
<div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20" style={{ overflow: 'visible', height: 'auto', padding: '24px' }}>
  <h2 className="text-xl font-semibold text-white" style={{ marginBottom: '16px' }}>Draft Board</h2>
  
  <div style={{ width: '100%', overflow: 'visible' }}>
    {Array.from({ length: Math.ceil(totalPicks / 4) }, (_, rowIndex) => (
      <div 
        key={rowIndex} 
        style={{ 
          display: 'flex', 
          marginBottom: '12px',
          gap: '12px',
          width: '100%',
          justifyContent: 'flex-start'
        }}
      >
        {draftBoard.slice(rowIndex * 4, (rowIndex + 1) * 4).map((slot) => (
          <div
            key={slot.visualPosition}
            className={`rounded-lg border-2 transition-all duration-300 ${
              slot.pick 
                ? (slot.pick === picks[picks.length - 1] && showResult
                    ? 'bg-green-500/30 border-green-400 scale-105'
                    : 'bg-blue-500/20 border-blue-400')
                : 'bg-white/10 border-white/30 border-dashed'
            }`}
            style={{ 
              width: 'calc(25% - 9px)',
              minWidth: '100px',
              height: '100px',
              padding: '12px',
              flexShrink: 0
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="text-lg font-bold text-yellow-400" style={{ marginBottom: '4px' }}>
                #{slot.pickNumber}
              </div>
              <div className="text-sm text-white/80" style={{ minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {slot.pick ? (
                  <div>
                    <div className="font-semibold text-white">
                      {slot.pick.player.name}
                    </div>
                    <div className="text-xs text-white/60">
                      {slot.pick.timestamp}
                    </div>
                  </div>
                ) : (
                  <div className="text-white/50 italic">
                    Waiting...
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
  
  {autoMode && showResult && picks.length < totalPicks && (
    <div className="text-center text-white/80 text-sm" style={{ marginTop: '16px' }}>
      Auto-continuing to next pick in 3 seconds...
    </div>
  )}
</div>
          </div>
        </div>
        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-white/20">
          <div className="text-center text-white/60 text-sm space-y-2">
            <div>
              <button 
                onClick={() => setShowModal('privacy')}
                className="hover:text-white mx-3 transition-colors cursor-pointer border-none bg-transparent"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowModal('terms')}
                className="hover:text-white mx-3 transition-colors cursor-pointer border-none bg-transparent"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => setShowModal('contact')}
                className="hover:text-white mx-3 transition-colors cursor-pointer border-none bg-transparent"
              >
                Contact
              </button>
            </div>
            <div>
              © {new Date().getFullYear()} DraftPickerPro™. All rights reserved.
            </div>
            <div className="text-xs text-white/40">
              DraftPickerPro™ is a trademark of DraftPickerPro. 
              Unauthorized use is prohibited.
            </div>
          </div>
        </footer>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl max-w-4xl max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-white/20 flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-white">{getModalContent()?.title}</h2>
                <button
                  onClick={() => setShowModal(null)}
                  className="text-white/60 hover:text-white text-2xl transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh] custom-scrollbar">
                {getModalContent()?.content}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default DraftPickerApp;